import {
	type OnboardingStep,
	type OnboardingStepDTO,
	OnboardingStepStatus,
	type TenantOnboardingDTO,
	type TenantOnboardingStepPatch,
} from '@today/types'
import { type MouseEvent, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSetOnboardingStep, useTenantOnboarding } from '@/hooks/useTenantOnboarding'
import { initialStep } from '@/modules/onboarding/wizard-navigation'
import {
	type OnboardingWizardRegistry,
	type OnboardingWizardStep,
	WizardStepKind,
} from '@/modules/onboarding/wizard-registry'

type SettableStatus = TenantOnboardingStepPatch['status']

/** The open step and what the page can do with it. */
export interface OnboardingWizardState {
	onboarding: TenantOnboardingDTO
	active: OnboardingStepDTO
	entry: OnboardingWizardStep
	index: number
	busy: boolean
	/** The status of the step update in flight, or null — tells the page which action is busy. */
	pendingStatus: SettableStatus | null
	/**
	 * The open step's last failed update, or null — once a later update succeeds, and
	 * whenever another step is open, so neither the error nor its Retry ever shows
	 * on a step it is not about (TO-733 REQ-006).
	 */
	saveError: unknown
	/** Re-sends the failed step update unchanged, through the same completion handling. */
	retrySave: () => void
	select: (step: OnboardingStep) => void
	move: (offset: number) => void
	complete: () => void
	doLater: () => void
	followLink: (event: MouseEvent<HTMLAnchorElement>, href: string) => void
}

/** REQ-009: opening a not-started embedded step records it as in progress, once per step. */
function useRecordEmbeddedStart(
	active: OnboardingStepDTO | undefined,
	entry: OnboardingWizardStep | null,
	record: (step: OnboardingStep) => void,
): void {
	const started = useRef(new Set<OnboardingStep>())
	useEffect(() => {
		if (!active || entry?.kind !== WizardStepKind.EMBEDDED) return
		if (active.status !== OnboardingStepStatus.NOT_STARTED || started.current.has(active.step)) return
		started.current.add(active.step)
		record(active.step)
	}, [active, entry, record])
}

/** "Mark as complete", "Do this later" (then advance) and following a link (in progress first when not started). */
function buildActions(
	active: OnboardingStepDTO,
	hasNext: boolean,
	update: (status: SettableStatus) => Promise<TenantOnboardingDTO | null>,
	move: (offset: number) => void,
	go: (href: string) => void,
): Pick<OnboardingWizardState, 'complete' | 'doLater' | 'followLink'> {
	return {
		complete: () => void update(OnboardingStepStatus.COMPLETE).catch(() => null),
		doLater: () =>
			void update(OnboardingStepStatus.SKIPPED)
				.then((next) => next && hasNext && move(1))
				.catch(() => null),
		followLink: (event, href) => {
			event.preventDefault()
			const started =
				active.status === OnboardingStepStatus.NOT_STARTED
					? update(OnboardingStepStatus.IN_PROGRESS).catch(() => null)
					: Promise.resolve(null)
			void started.then(() => go(href))
		},
	}
}

type ReadQuery = Pick<ReturnType<typeof useTenantOnboarding>, 'isPending' | 'isFetching' | 'isError' | 'error'>
type StepMutation = ReturnType<typeof useSetOnboardingStep>

/**
 * TO-733 REQ-005/006: the result while no step can be shown. A retry after a
 * failed read keeps the error status while it fetches, so that shows loading,
 * not the error.
 */
function unloadedResult(query: ReadQuery, retryLoad: () => void): OnboardingWizardResult {
	const isLoading = query.isPending || query.isFetching
	return { isLoading, loadError: !isLoading && query.isError ? query.error : null, retryLoad, state: null }
}

/** The failed update when it was for `step` — a failure on another step is not this step's to show or retry. */
function failedUpdateFor(setStep: StepMutation, step: OnboardingStep): TenantOnboardingStepPatch | undefined {
	return setStep.isError && setStep.variables?.step === step ? setStep.variables : undefined
}

/** TO-733 REQ-006/007: which update is in flight, and the open step's failed update, if any. */
function writeStatus(
	setStep: StepMutation,
	step: OnboardingStep,
): Pick<OnboardingWizardState, 'busy' | 'pendingStatus' | 'saveError'> {
	return {
		busy: setStep.isPending,
		pendingStatus: setStep.isPending ? (setStep.variables?.status ?? null) : null,
		saveError: failedUpdateFor(setStep, step) ? setStep.error : null,
	}
}

/** The wizard's read: loading (incl. a retry in flight), failed, or the state. */
export interface OnboardingWizardResult {
	isLoading: boolean
	/** The read's failure while no retry is in flight, or null (TO-733 REQ-006). */
	loadError: unknown
	retryLoad: () => void
	state: OnboardingWizardState | null
}

/**
 * The onboarding wizard's state and actions (TO-190 REQ-009–REQ-011): which step
 * is open (pinned on load, then the admin's choice), and the writes behind "Mark
 * as complete", "Do this later" and following a link. The write that completes
 * onboarding leaves the wizard for `/admin`. `state` is `null` while loading or
 * unloadable.
 */
export function useOnboardingWizard(slug: string, registry: OnboardingWizardRegistry): OnboardingWizardResult {
	const navigate = useNavigate()
	const query = useTenantOnboarding(slug)
	const setStep = useSetOnboardingStep(slug)
	const [chosen, setChosen] = useState<OnboardingStep | null>(null)
	const landing = useRef<OnboardingStep | null>(null)

	const onboarding = query.data
	if (onboarding && landing.current === null) landing.current = initialStep(onboarding.steps)
	const active = onboarding?.steps.find((s) => s.step === (chosen ?? landing.current))
	const entry = active ? registry[active.step] : null
	const { mutate } = setStep
	useRecordEmbeddedStart(active, entry, (step) => mutate({ step, status: OnboardingStepStatus.IN_PROGRESS }))

	const retryLoad = () => void query.refetch()
	if (!onboarding || !active || !entry) return unloadedResult(query, retryLoad)
	const index = onboarding.steps.indexOf(active)
	const move = (offset: number) => setChosen(onboarding.steps[index + offset].step)

	const send = async (body: TenantOnboardingStepPatch): Promise<TenantOnboardingDTO | null> => {
		const next = await setStep.mutateAsync(body)
		if (onboarding.completedAt !== null || next.completedAt === null) return next
		void navigate('/admin')
		return null
	}
	const update = (status: SettableStatus) => send({ step: active.step, status })
	const failed = failedUpdateFor(setStep, active.step)
	const retrySave = () => {
		if (failed) void send(failed).catch(() => null)
	}
	const actions = buildActions(active, index < onboarding.steps.length - 1, update, move, (href) => void navigate(href))
	const state = { onboarding, active, entry, index, ...writeStatus(setStep, active.step), retrySave }
	return { isLoading: false, loadError: null, retryLoad, state: { ...state, ...actions, select: setChosen, move } }
}
